/* eslint-disable max-len*/
import { ReactElement, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ActionButton from 'src/components/ActionButton/ActionButton';
import { useConfirmationModalContext } from 'src/hooks/useConfirmationModalContext';
import { createNotification } from 'src/redux/slices/notificationSlice';
import StyledSingleDropdown from 'src/components/StyledSingleDropdown/StyledSingleDropdown';
import {
  selectExitAdjustmentFormViewModel,
  selectExitAdjustmentListItems,
  selectExitAdjustmentLoadingFormViewModel,
  selectExitAdjustmentLoadingListItems,
  setExitAdjustmentDataEntryMode,
  setExitAdjustmentLea,
  setExitAdjustmentSchool,
  setExitAdjustmentSchoolYear,
  setExitAdjustmentStatus,
} from 'src/redux/slices/exitAdjustmentSlice';
import {
  fetchExitAdjustmentLeas,
  fetchExitAdjustmentListItems,
  fetchExitAdjustmentSchoolsByLeaId,
  fetchExitAdjustmentSchoolYears,
  fetchExitAdjustmentStudentUpdate,
} from 'src/redux/thunks/exitAdjustmentThunks';
import { ButtonSize } from 'src/types/ButtonSize';
import { ApiResponseModel } from 'src/types/ApiResponseModel';
import { ExitAdjustmentListItemModel } from 'src/types/ExitAdjustmentListItemModel';
import { isStateHelpdesk } from 'src/utilities/userUtilities';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { TooltipAnchor } from '../../../../services/app.service';
import {
  abortPromiseOnUnmount,
  isValidCode,
} from '../../../../services/base.service';
import { deleteExitAdjustment } from '../../../../services/exitAdjustment.service';
import { appRoutePaths } from '../../../../services/route.service';
import { DataEntryMode } from '../../../../types/DataEntryMode';
import Student from '../StudentPage';
import './ExitAdjustmentList.css';

const ExitAdjustmentList = (): ReactElement => {
  const dispatch = useAppDispatch();
  const confirmContext = useConfirmationModalContext();
  const navigate = useNavigate();
  const location = useLocation();
  const formViewModel = useAppSelector(selectExitAdjustmentFormViewModel);
  const listItemsIsLoading = useAppSelector(
    selectExitAdjustmentLoadingListItems
  );
  const formViewModelIsLoading = useAppSelector(
    selectExitAdjustmentLoadingFormViewModel
  );
  const listItems: ExitAdjustmentListItemModel[] = useAppSelector(
    selectExitAdjustmentListItems
  );
  const [isSchoolDisabled, setIsSchoolDisabled] = useState(true);

  const searchParams = new URLSearchParams(location.search);
  const urlSchoolYear = searchParams.get('schoolYear');
  const urlLea = searchParams.get('lea');
  const urlSchool = searchParams.get('school');
  const urlStatus = searchParams.get('status');

  const getSchoolYearStartYear = (schoolYearValue: string): number | null => {
    const yearMatch = schoolYearValue.match(/\d{4}/);
    return yearMatch ? Number(yearMatch[0]) : null;
  };

  const getDefaultSelectedSchoolYear = ():
    | { value: string; label: string }
    | undefined => {
    const schoolYearOptions = formViewModel?.schoolYearOptions || [];
    const defaultSchoolYearOption =
      schoolYearOptions[1] || schoolYearOptions[0];
    if (defaultSchoolYearOption) {
      return {
        value: defaultSchoolYearOption.value,
        label: defaultSchoolYearOption.text || defaultSchoolYearOption.value,
      };
    }

    return undefined;
  };

  const updateURLWithFilters = (
    schoolYear: string,
    lea: string,
    school: string,
    status: string
  ): void => {
    const newSearchParams = new URLSearchParams();
    if (schoolYear) newSearchParams.set('schoolYear', schoolYear);
    if (lea) newSearchParams.set('lea', lea);
    if (school) newSearchParams.set('school', school);
    if (status) newSearchParams.set('status', status);

    const newURL = `${location.pathname}${newSearchParams.toString() ? `?${newSearchParams.toString()}` : ''
      }`;
    navigate(newURL, { replace: true });
  };

  // using same reviewStatusOptions as input page, but needs to be updated
  const getFormViewModelReviewStatusOptions = formViewModel?.statusOptions.map(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (obj: any) => {
      const keys = Object.keys(obj) as (keyof typeof obj)[];
      const lastKey = keys[keys.length - 1];
      // Create a shallow copy and delete the last key
      const rest = { ...obj };
      delete rest[lastKey];
      return rest;
    }
  );

  useEffect(() => {
    if (formViewModel?.schoolYearOptions.length > 0) {
      let selectedSchoolYear = getDefaultSelectedSchoolYear();
      if (urlSchoolYear) {
        const foundSchoolYear = formViewModel.schoolYearOptions.find(
          (option: { value: string }) => option.value === urlSchoolYear
        );
        if (foundSchoolYear) {
          selectedSchoolYear = {
            value: foundSchoolYear.value,
            label: foundSchoolYear.text,
          };
        }
      }

      if (
        selectedSchoolYear &&
        (!formViewModel.selectedSchoolYear ||
          formViewModel.selectedSchoolYear.value !== selectedSchoolYear.value)
      ) {
        dispatch(setExitAdjustmentSchoolYear(selectedSchoolYear));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, formViewModel?.schoolYearOptions, urlSchoolYear]);

  useEffect(() => {
    if (urlLea && formViewModel?.leaOptions.length > 0) {
      const foundLea = formViewModel.leaOptions.find(
        (option: { value: string }) => option.value === urlLea
      );
      if (
        foundLea &&
        (!formViewModel.selectedLea ||
          formViewModel.selectedLea.value !== foundLea.value)
      ) {
        dispatch(
          setExitAdjustmentLea({
            value: foundLea.value,
            label: foundLea.text,
          })
        );
        setIsSchoolDisabled(false);
        dispatch(fetchExitAdjustmentSchoolsByLeaId(foundLea.value));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, formViewModel?.leaOptions, urlLea]);

  useEffect(() => {
    setIsSchoolDisabled(!formViewModel?.selectedLea?.value);
  }, [formViewModel?.selectedLea?.value]);

  useEffect(() => {
    if (urlSchool && urlLea && formViewModel?.schoolOptions.length > 0) {
      const foundSchool = formViewModel.schoolOptions.find(
        (option: { value: string }) => option.value === urlSchool
      );
      if (
        foundSchool &&
        (!formViewModel.selectedSchool ||
          formViewModel.selectedSchool.value !== foundSchool.value)
      ) {
        dispatch(
          setExitAdjustmentSchool({
            value: foundSchool.value,
            label: foundSchool.text,
          })
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, formViewModel?.schoolOptions, urlSchool, urlLea]);

  useEffect(() => {
    if (urlStatus && formViewModel?.statusOptions.length > 0) {
      const foundStatus = formViewModel.statusOptions.find(
        (option: { value: string }) => option.value === urlStatus
      );
      if (
        foundStatus &&
        (!formViewModel.selectedStatus ||
          formViewModel.selectedStatus.value !== foundStatus.value)
      ) {
        dispatch(
          setExitAdjustmentStatus({
            value: foundStatus.value,
            label: foundStatus.text,
          })
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, formViewModel?.statusOptions, urlStatus]);

  // Fetch school year and lea options first
  useEffect(() => {
    const promise: unknown = dispatch(fetchExitAdjustmentSchoolYears());
    return () => {
      abortPromiseOnUnmount(promise);
    };
  }, [dispatch]);

  useEffect(() => {
    const schoolYear =
      formViewModel?.selectedSchoolYear?.value ||
      urlSchoolYear ||
      getDefaultSelectedSchoolYear()?.value;
    if (schoolYear) {
      const promise: unknown = dispatch(
        fetchExitAdjustmentStudentUpdate(schoolYear)
      );
      return () => {
        abortPromiseOnUnmount(promise);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatch,
    formViewModel?.selectedSchoolYear?.value,
    urlSchoolYear,
    formViewModel?.schoolYearOptions,
  ]);

  useEffect(() => {
    const promise: unknown = dispatch(fetchExitAdjustmentLeas());
    return () => {
      abortPromiseOnUnmount(promise);
    };
  }, [dispatch]);

  useEffect(() => {
    return () => {
      dispatch(setExitAdjustmentLea({ value: '', label: '' }));
      dispatch(setExitAdjustmentSchool({ value: '', label: '' }));
    };
  }, [dispatch]);

  useEffect(() => {
    if (formViewModel?.schoolYearOptions.length > 0) {
      const schoolYear =
        urlSchoolYear || getDefaultSelectedSchoolYear()?.value || '';
      const lea = urlLea || '';
      const school = urlSchool || '';
      const status = urlStatus || '';

      const promise: unknown = dispatch(
        fetchExitAdjustmentListItems({
          schoolYear,
          lea,
          school,
          status,
        })
      );
      return () => {
        abortPromiseOnUnmount(promise);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatch,
    formViewModel?.schoolYearOptions.length,
    urlSchoolYear,
    urlLea,
    urlSchool,
    urlStatus,
  ]);

  const selectedSchoolYearOption = {
    value: formViewModel?.selectedSchoolYear?.value || '',
    label: formViewModel?.selectedSchoolYear?.label || '',
  };

  const selectedLeaOption =
    formViewModel?.selectedLea && formViewModel.selectedLea.value
      ? formViewModel.selectedLea
      : null;

  const selectedSchoolOption =
    formViewModel?.selectedSchool && formViewModel.selectedSchool.value
      ? formViewModel.selectedSchool
      : null;

  const selectedReviewStatusOption =
    formViewModel?.selectedStatus && formViewModel.selectedStatus.value
      ? formViewModel.selectedStatus
      : null;

  const selectedSchoolYearValue =
    formViewModel?.selectedSchoolYear?.value || '';
  const defaultSchoolYearValue = formViewModel?.defaultSchoolYear?.value || '';
  const selectedSchoolYearStart = getSchoolYearStartYear(
    selectedSchoolYearValue
  );
  const defaultSchoolYearStart = getSchoolYearStartYear(defaultSchoolYearValue);
  const isPreviousSchoolYear =
    selectedSchoolYearStart !== null &&
    defaultSchoolYearStart !== null &&
    selectedSchoolYearStart === defaultSchoolYearStart - 1;
  const allowEditForCurrentUser = isPreviousSchoolYear;

  const handleEdit = (id: string): void => {
    const currentFilters = {
      schoolYear: formViewModel?.selectedSchoolYear?.value || '',
      lea: formViewModel?.selectedLea?.value || '',
      school: formViewModel?.selectedSchool?.value || '',
      status: formViewModel?.selectedStatus?.value || '',
    };

    const searchURLParams = new URLSearchParams();
    if (currentFilters.schoolYear)
      searchURLParams.set('schoolYear', currentFilters.schoolYear);
    if (currentFilters.lea) searchURLParams.set('lea', currentFilters.lea);
    if (currentFilters.school)
      searchURLParams.set('school', currentFilters.school);
    if (currentFilters.status)
      searchURLParams.set('status', currentFilters.status);

    const queryString = searchURLParams.toString();
    const inputUrl = `${appRoutePaths.ExitAdjustmentInput(id)}${queryString ? `?${queryString}` : ''
      }`;

    navigate(inputUrl, {
      state: {
        filters: currentFilters,
        allowEdit: allowEditForCurrentUser,
      },
    });
  };

  const handleDelete = async (id: string): Promise<void> => {
    confirmContext.setOptions({
      title: 'Delete Exit Adjustment',
      content: (
        <div className="confirm--message">
          Are you sure you want to delete this Exit Adjustment?
        </div>
      ),
      confirmText: 'Yes',
      cancelText: 'No',
      onOk: async (): Promise<ApiResponseModel<unknown>> => {
        const response = await deleteExitAdjustment(id);
        if (isValidCode(response.status)) {
          dispatch(
            createNotification({
              severity: 'success',
              children: 'Exit Adjustment deleted',
            })
          );
          dispatch(
            fetchExitAdjustmentListItems({
              schoolYear: formViewModel?.selectedSchoolYear?.value || '',
              lea: formViewModel?.selectedLea?.value || '',
              school: formViewModel?.selectedSchool?.value || '',
              status: formViewModel?.selectedStatus?.value || '',
            })
          );
        }
        return response;
      },
    });
    await confirmContext.showConfirmation();
  };

  const handleResetFilter = (): void => {
    const defaultSchoolYear = getDefaultSelectedSchoolYear();
    const schoolYearValue = defaultSchoolYear?.value || '';

    if (defaultSchoolYear) {
      dispatch(setExitAdjustmentSchoolYear(defaultSchoolYear));
    }
    dispatch(setExitAdjustmentLea({ value: '', label: '' }));
    dispatch(setExitAdjustmentSchool({ value: '', label: '' }));
    dispatch(setExitAdjustmentStatus({ value: '', label: '' }));

    updateURLWithFilters(schoolYearValue, '', '', '');

    dispatch(
      fetchExitAdjustmentListItems({
        schoolYear: schoolYearValue,
        lea: '',
        school: '',
        status: '',
      })
    );
    setIsSchoolDisabled(true);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleOnChangeOfSchoolYear = async (option: any): Promise<void> => {
    if (option) {
      dispatch(setExitAdjustmentSchoolYear(option));
    }
  };

  // eslint-disable-next-line  @typescript-eslint/no-explicit-any
  const handleOnLeaChange = async (option: any): Promise<void> => {
    if (!option) {
      dispatch(setExitAdjustmentLea({ value: '', label: '' }));
      dispatch(setExitAdjustmentSchool({ value: '', label: '' }));
      const schoolYear = formViewModel?.selectedSchoolYear?.value || '';
      const status = formViewModel?.selectedStatus?.value || '';
      updateURLWithFilters(schoolYear, '', '', status);
      setIsSchoolDisabled(true);
    } else {
      setIsSchoolDisabled(false);
      dispatch(setExitAdjustmentLea(option));
      dispatch(setExitAdjustmentSchool({ value: '', label: '' }));
      const schoolYear = formViewModel?.selectedSchoolYear?.value || '';
      const status = formViewModel?.selectedStatus?.value || '';
      updateURLWithFilters(schoolYear, option?.value || '', '', status);
      dispatch(fetchExitAdjustmentSchoolsByLeaId(option?.value));
    }
  };

  // eslint-disable-next-line  @typescript-eslint/no-explicit-any
  const handleOnChangeOfSchool = async (option: any): Promise<void> => {
    option && dispatch(setExitAdjustmentSchool(option));
  };

  // eslint-disable-next-line  @typescript-eslint/no-explicit-any
  const handleOnChangeOfReviewStatus = async (option: any): Promise<void> => {
    option && dispatch(setExitAdjustmentStatus(option));
  };

  const handleApplyFilter = (): void => {
    const schoolYear = formViewModel?.selectedSchoolYear?.value || '';
    const lea = formViewModel?.selectedLea?.value || '';
    const school = formViewModel?.selectedSchool?.value || '';
    const status = formViewModel?.selectedStatus?.value || '';

    updateURLWithFilters(schoolYear, lea, school, status);

    dispatch(
      fetchExitAdjustmentListItems({
        schoolYear,
        lea,
        school,
        status,
      })
    );
  };

  const handleNew = (): void => {
    const allowEdit = formViewModel?.studentUpdate || false;
    dispatch(setExitAdjustmentDataEntryMode(DataEntryMode.NEW));

    const currentFilters = {
      schoolYear: formViewModel?.selectedSchoolYear?.value || '',
      lea: formViewModel?.selectedLea?.value || '',
      school: formViewModel?.selectedSchool?.value || '',
      status: formViewModel?.selectedStatus?.value || '',
    };

    const searchParamsURL = new URLSearchParams();
    if (currentFilters.schoolYear)
      searchParamsURL.set('schoolYear', currentFilters.schoolYear);
    if (currentFilters.lea) searchParamsURL.set('lea', currentFilters.lea);
    if (currentFilters.school)
      searchParamsURL.set('school', currentFilters.school);
    if (currentFilters.status)
      searchParamsURL.set('status', currentFilters.status);

    const queryString = searchParamsURL.toString();
    const inputUrl = `${appRoutePaths.ExitAdjustmentInput('new')}${queryString ? `?${queryString}` : ''
      }`;

    navigate(inputUrl, {
      state: {
        allowEdit: allowEdit,
        filters: currentFilters,
      },
    });
  };

  return (
    <Student
      pageClass="data-input--student--exit-adjustment--list"
      isLoading={listItemsIsLoading || formViewModelIsLoading}
      loadingDataId={'exitAdjustment-list-loader'}
    >
      <div className="content-outer-container">
        <div className="content-inner-container">
          <div className="subheading-and-button-container">
            <h4 data-testid={'component-heading-text'}>Exit Adjustment</h4>
          </div>
          <div className="filters-selection-container">
            <div className="dropdown-selection">
              <StyledSingleDropdown
                displayName="School Year"
                showLabel={true}
                labelText="School Year"
                fieldName="schoolYear"
                isClearable={false}
                isSearchable={false}
                options={formViewModel?.schoolYearOptions || []}
                value={selectedSchoolYearOption}
                onChange={handleOnChangeOfSchoolYear}
              />
            </div>
            <div className="dropdown-selection">
              <StyledSingleDropdown
                displayName="LEA"
                showLabel={true}
                labelText="LEA"
                fieldName="lea"
                isClearable={false}
                isSearchable={true}
                options={formViewModel?.leaOptions || []}
                value={selectedLeaOption}
                onChange={handleOnLeaChange}
                placeholder="Select..."
              />
            </div>
            <div className="dropdown-selection">
              <StyledSingleDropdown
                displayName="School"
                showLabel={true}
                fieldName="school"
                labelText="School"
                isClearable={false}
                isSearchable={true}
                options={formViewModel?.schoolOptions || []}
                value={selectedSchoolOption}
                disabled={isSchoolDisabled}
                onChange={handleOnChangeOfSchool}
                placeholder="Select..."
              />
            </div>
            <div className="dropdown-selection">
              <StyledSingleDropdown
                displayName="Status"
                labelText="Status"
                showLabel={true}
                fieldName="reviewStatus"
                isClearable={false}
                isSearchable={true}
                options={getFormViewModelReviewStatusOptions || []}
                value={selectedReviewStatusOption}
                onChange={handleOnChangeOfReviewStatus}
                placeholder="Select..."
              />
            </div>
            <div className="button-group">
              <ActionButton
                classes="button apply-button"
                onClick={handleApplyFilter}
                dataTestId="apply-button"
                tooltipText="Apply Filter"
              >
                <span>Apply Filter</span>
              </ActionButton>
              <ActionButton
                classes="button reset-button"
                onClick={handleResetFilter}
                dataTestId="reset-button"
                tooltipText="Reset Filter"
              >
                <span>Reset Filter</span>
              </ActionButton>
            </div>
          </div>
          <div className="subheading-and-button-container">
            <h4 data-testid={'component-heading-text'}></h4>
            <ActionButton
              onClick={handleNew}
              tooltipText="Exit Adjustment Request"
              classes="button--secondary"
              size={ButtonSize.SMALL}
              disabled={
                isStateHelpdesk() || !(formViewModel?.studentUpdate || false)
              }
            >
              <>Request New</>
            </ActionButton>
          </div>
          <div className="table-container">
            <table cellSpacing={0} className="page-view-table">
              <thead>
                <tr>
                  <th>Student Name</th>
                  <th>MSIS ID</th>
                  <th>LEA</th>
                  <th>School Name</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {listItems?.length > 0 ? (
                  listItems.map((exitAdjustment) => (
                    <tr
                      key={`${exitAdjustment.id}`}
                      data-testid={exitAdjustment.id}
                    >
                      <td>
                        {exitAdjustment.studentLastOrSurname},{' '}
                        {exitAdjustment.studentFirstName}
                      </td>
                      <td>{exitAdjustment.studentIdentifierState}</td>
                      <td>{exitAdjustment.districtName}</td>
                      <td>{exitAdjustment.schoolName}</td>
                      <td>{exitAdjustment.reviewStatus}</td>
                      <td>
                        <div className="action--column">
                          <button
                            data-testid={`edit-button-${exitAdjustment.id}`}
                            aria-label="Edit"
                            onClick={() => handleEdit(exitAdjustment.id)}
                            data-tooltip-id={TooltipAnchor}
                            data-tooltip-content={
                              isStateHelpdesk()
                                ? 'View Exit Adjustment'
                                : allowEditForCurrentUser
                                  ? 'Edit/View Exit Adjustment'
                                  : 'View Exit Adjustment'
                            }
                          >
                            <img src={'/images/icon-edit.svg'} />
                          </button>
                          {exitAdjustment.reviewStatus.toUpperCase() ===
                            'DENIED' &&
                            (formViewModel?.studentUpdate || false) && (
                            <button
                              data-testid={`delete-button-${exitAdjustment.id}`}
                              aria-label="Delete"
                              onClick={() => handleDelete(exitAdjustment.id)}
                              data-tooltip-id={TooltipAnchor}
                              data-tooltip-content="Delete Exit Adjustment"
                            >
                              <img src={'/images/icon-trash copy.svg'} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr className="no-results-row">
                    <td colSpan={6}>No results match your search criteria</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Student>
  );
};

export default ExitAdjustmentList;
